import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";

const useAuthService = () => {
  const {
    loginWithRedirect,
    logout,
    user,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
  } = useAuth0();

  const storeLastVisitedRoute = () => {
    if (window.location.pathname === "/signup") {
      sessionStorage.setItem("redirectPath", "/");
    } else {
      sessionStorage.setItem("redirectPath", window.location.pathname);
    }
  };

  const login = useCallback(
    async (loginHintEmail?: string) => {
      try {
        storeLastVisitedRoute();
        await loginWithRedirect({
          authorizationParams: {
            screen_hint: "login",
            ...(loginHintEmail?.trim() ? { login_hint: loginHintEmail } : {}),
          },
        });
      } catch (error) {
        console.error("Login redirection failed:", error);
      }
    },
    [loginWithRedirect]
  );

  const signup = useCallback(
    async (loginHintEmail?: string) => {
      try {
        storeLastVisitedRoute();
        await loginWithRedirect({
          authorizationParams: {
            screen_hint: "signup",
            ...(loginHintEmail?.trim() ? { login_hint: loginHintEmail } : {}),
          },
        });
      } catch (error) {
        console.error("Signup redirection failed:", error);
      }
    },
    [loginWithRedirect]
  );

  const logoutUser = useCallback(() => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  const getLinkedIdentities = useCallback(async () => {
    if (!user?.sub) return [];

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      });

      const response = await fetch(
        `${process.env.REACT_APP_AUTH0_AUDIENCE}users/${encodeURIComponent(
          user.sub
        )}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch user identities");
      }

      const userData = await response.json();
      return userData.identities || [];
    } catch (err) {
      console.error("Error fetching linked identities:", err);
      return [];
    }
  }, [user?.sub, getAccessTokenSilently]);

  const getUser = useCallback(() => user, [user]);

  return {
    login,
    signup,
    logout: logoutUser,
    getUser,
    isAuthenticated,
    getToken: getAccessTokenSilently,
    isLoading,
    getLinkedIdentities,
  };
};

export default useAuthService;
